import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

/**
 * Интерфейс для недельного сравнения план vs факт
 * Соответствует VIEW studio_weekly_plan_vs_fact
 */
export interface WeeklyPlanVsFactRow {
  // Идентификаторы и временные рамки
  studio_internal_id: number
  period_id: number
  week_id: number
  week_number_in_period: number
  week_start: string
  week_end: string
  week_len_days: number
  partner_number: number | null
  
  // ===== ПЛАН (недельные значения) =====
  
  // Основные метрики воронки
  weekly_leads_plan: number | null
  weekly_appointments_plan: number | null
  weekly_new_visitors_plan: number | null
  weekly_new_sales_plan: number | null
  weekly_new_sales_amount_plan: number | null
  weekly_new_sales_aov_plan: number | null
  
  // Продления
  weekly_renewals_count_plan: number | null
  weekly_renewals_amount_plan: number | null
  weekly_renewals_aov_plan: number | null
  
  // Апгрейды
  weekly_upgrades_count_plan: number | null
  weekly_upgrades_amount_plan: number | null
  weekly_upgrades_aov_plan: number | null
  
  // Возвращающиеся клиенты
  weekly_returning_clients_count_plan: number | null
  weekly_returning_clients_amount_plan: number | null
  weekly_returning_clients_aov_plan: number | null
  
  // Персональные сессии
  weekly_private_sessions_count_plan: number | null
  weekly_private_sessions_amount_plan: number | null
  weekly_private_sessions_aov_plan: number | null
  
  // Финансовые метрики
  weekly_gross_revenue_plan: number | null
  weekly_marketing_budget_plan: number | null
  weekly_cost_per_lead_plan: number | null
  weekly_refunds_amount_plan: number | null
  
  // Конверсии (проценты)
  booking_conversion_plan: number | null
  visit_conversion_plan: number | null
  sales_conversion_plan: number | null
  
  // Подписки - новые
  weekly_membership_fees_new_plan: number | null
  weekly_new_subscriptions_count_plan: number | null
  weekly_new_subscriptions_amount_plan: number | null
  weekly_new_subscriptions_aov_plan: number | null
  weekly_membership_fees_subscription_upgrades_plan: number | null
  weekly_membership_fees_subscription_returning_plan: number | null
  
  // Подписки - продления
  weekly_subscription_renewals_count_plan: number | null
  weekly_subscription_renewals_amount_plan: number | null
  weekly_subscription_renewals_aov_plan: number | null
  
  // Подписки - апгрейды
  weekly_subscription_upgrades_count_plan: number | null
  weekly_subscription_upgrades_amount_plan: number | null
  weekly_subscription_upgrades_aov_plan: number | null
  
  // Подписки - возвращающиеся клиенты
  weekly_subscription_returning_clients_count_plan: number | null
  weekly_subscription_returning_clients_amount_plan: number | null
  weekly_subscription_returning_clients_aov_plan: number | null
  
  // ===== ФАКТ (недельные значения) =====
  
  // Основные метрики воронки
  weekly_leads_fact: number | null
  weekly_appointments_fact: number | null
  weekly_new_visitors_fact: number | null
  weekly_new_sales_fact: number | null
  weekly_new_sales_amount_fact: number | null
  weekly_avg_sales_check_fact: number | null
  
  // Продления
  weekly_renewals_count_fact: number | null
  weekly_renewals_amount_fact: number | null
  
  // Апгрейды
  weekly_upgrades_count_fact: number | null
  weekly_upgrades_amount_fact: number | null
  
  // Возвращающиеся клиенты
  weekly_returning_clients_count_fact: number | null
  weekly_returning_clients_amount_fact: number | null
  
  // Персональные сессии
  weekly_private_sessions_count_fact: number | null
  weekly_private_sessions_amount_fact: number | null
  
  // Финансовые метрики
  weekly_gross_revenue_fact: number | null
  weekly_marketing_expenses_amount_fact: number | null
  weekly_refunds_amount_fact: number | null
  weekly_trial_payments_amount_fact: number | null
  weekly_other_income_fact: number | null

  // Конверсии факт
  booking_conversion_fact: number | null
  visit_conversion_fact: number | null
  sales_conversion_fact: number | null

  // Подписки - новые
  weekly_membership_fees_new_amount_fact: number | null
  weekly_new_subscriptions_count_fact: number | null
  weekly_new_subscriptions_amount_fact: number | null
  weekly_membership_fees_subscription_upgrades_amount_fact: number | null
  weekly_membership_fees_subscription_returning_amount_fact: number | null
  
  // Подписки - продления
  weekly_subscription_renewals_count_fact: number | null
  weekly_subscription_renewals_amount_fact: number | null
  
  // Подписки - апгрейды
  weekly_subscription_upgrades_count_fact: number | null
  weekly_subscription_upgrades_amount_fact: number | null
  
  // Подписки - возвращающиеся клиенты
  weekly_subscription_returning_clients_count_fact: number | null
  weekly_subscription_returning_clients_amount_fact: number | null
  
  // ===== ПРОЦЕНТ ВЫПОЛНЕНИЯ =====
  
  leads_completion_pct: number | null
  appointments_completion_pct: number | null
  new_visitors_completion_pct: number | null
  sales_completion_pct: number | null
  sales_amount_completion_pct: number | null
  revenue_completion_pct: number | null
  renewals_count_completion_pct: number | null
  renewals_amount_completion_pct: number | null
  
  // ===== МЕТАДАННЫЕ =====
  
  days_with_data: number
  confirmed_days: number
  plan_created_at: string | null
  plan_updated_at: string | null
}

export interface UseWeeklyPlanVsFactParams {
  studioId: number | null
  periodId: number | null
}

export interface UseWeeklyPlanVsFactReturn {
  weeks: WeeklyPlanVsFactRow[]
  loading: boolean
  error: string | null
  refresh: () => void
}

/**
 * Hook для загрузки недельного сравнения план vs факт
 * Использует VIEW studio_weekly_plan_vs_fact - никаких преобразований на фронтенде
 * 
 * @example
 * const { weeks, loading, error } = useWeeklyPlanVsFact({
 *   studioId: 1,
 *   periodId: 123
 * })
 * 
 * // Данные уже недельные, без преобразований!
 * weeks.forEach(week => {
 *   console.log(`Неделя ${week.week_number_in_period}:`)
 *   console.log(`  План: ${week.weekly_new_sales_plan} продаж`)
 *   console.log(`  Факт: ${week.weekly_new_sales_fact} продаж`)
 *   console.log(`  Выполнение: ${week.sales_completion_pct}%`)
 * })
 */
export function useWeeklyPlanVsFact({
  studioId,
  periodId
}: UseWeeklyPlanVsFactParams): UseWeeklyPlanVsFactReturn {
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery<WeeklyPlanVsFactRow[], Error>({
    queryKey: ['weekly-plan-vs-fact', studioId, periodId],
    queryFn: async () => {
      if (!studioId || !periodId) {
        return []
      }
      
      const { data, error } = await supabase
        .from('studio_weekly_plan_vs_fact')
        .select('*')
        .eq('studio_internal_id', studioId)
        .eq('period_id', periodId)
        .order('week_number_in_period')
      
      if (error) {
        throw new Error(`Failed to load weekly plan vs fact: ${error.message}`)
      }
      
      return (data || []) as WeeklyPlanVsFactRow[]
    },
    enabled: Boolean(studioId && periodId),
    staleTime: 5 * 60 * 1000, // Кэш на 5 минут
    gcTime: 10 * 60 * 1000, // Хранить в памяти 10 минут
  })

  return {
    weeks: data || [],
    loading: isLoading,
    error: error ? (error as Error).message : null,
    refresh: () => { refetch() }
  }
}
